/**
 * OfferSettings — configure the offer, pain points, and credibility signals.
 *
 * Fetches current offer config on mount and saves via PUT /api/settings/offer.
 * All state is immutable; handlers derive new objects from previous state.
 */

import { useState, useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2, Sparkles, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChipInput } from '@/components/chip-input'
import { FieldHint } from '@/components/field-hint'
import { useApi } from '@/hooks/use-api'
import type { OfferConfig } from '@dealcloser/shared'
import { DEFAULT_OFFER } from '@dealcloser/shared'
import { TrackedLinksCard } from './tracked-links-card'

// ============================================================
// Constants
// ============================================================

const offerKeys = { config: ['settings', 'offer'] as const }

const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-subtle-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none'

// ============================================================
// Component
// ============================================================

export default function OfferSettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [form, setForm] = useState<OfferConfig>(DEFAULT_OFFER)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: offerKeys.config,
    queryFn: async (): Promise<OfferConfig> => {
      const res = await apiFetch<OfferConfig>('/api/settings/offer')
      if (!res.success || !res.data) return DEFAULT_OFFER
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: OfferConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/offer', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offerKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  const setPainPoints = useCallback((painPoints: readonly string[]) => {
    setForm((prev) => ({ ...prev, painPoints }))
  }, [])

  // --- Auto-fill from URL ---
  const [offerUrl, setOfferUrl] = useState('')
  const [autoFillSuccess, setAutoFillSuccess] = useState(false)

  const analyzeUrlMutation = useMutation({
    mutationFn: async (url: string) => {
      const res = await apiFetch<{
        company_name?: string
        value_proposition?: string
        target_audience?: {
          job_titles?: string[]
          industries?: string[]
          pain_points?: string[]
        }
        testimonials?: string[]
        competitors?: string[]
        pricing_model?: string
      }>('/api/ai/analyze-url', { method: 'POST', body: { url } })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to analyze URL')
      return res.data
    },
    onSuccess: (analysis) => {
      setForm((prev) => ({
        ...prev,
        offerDescription: analysis.value_proposition || prev.offerDescription,
        primaryPainPoint: analysis.target_audience?.pain_points?.[0] || prev.primaryPainPoint,
        painPoints: analysis.target_audience?.pain_points?.slice(1) ?? prev.painPoints,
        socialProof: analysis.testimonials?.join(' | ') || prev.socialProof,
        targetAudience: analysis.target_audience?.job_titles?.join(', ') || prev.targetAudience,
      }))
      setAutoFillSuccess(true)
      setTimeout(() => setAutoFillSuccess(false), 5000)
    },
  })

  const isAnalyzing = analyzeUrlMutation.isPending
  const analyzeError = analyzeUrlMutation.error instanceof Error ? analyzeUrlMutation.error.message : null

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null

  return (
    <div className="space-y-6">
      {/* Auto-fill from URL */}
      <Card className="border-primary/20 bg-primary/[0.02]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cobalt-bright" />
            Auto-fill from Offer URL
          </CardTitle>
          <CardDescription>
            Paste your offer or landing page URL and AI will analyze it to auto-fill your offer description, pain points, ICP criteria, and more.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="https://yourcompany.com/offer"
                value={offerUrl}
                onChange={(e) => setOfferUrl(e.target.value)}
                disabled={isAnalyzing}
                className="pl-9"
              />
            </div>
            <Button
              onClick={() => analyzeUrlMutation.mutate(offerUrl)}
              disabled={isAnalyzing || !offerUrl.trim()}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Auto-fill
                </>
              )}
            </Button>
          </div>
          {analyzeError && (
            <p className="mt-2 text-sm text-destructive">{analyzeError}</p>
          )}
          {autoFillSuccess && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-emerald-300">
              <CheckCircle2 className="h-4 w-4" />
              Fields auto-filled from URL. Review below and save.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Your Offer */}
      <Card>
        <CardHeader>
          <CardTitle>Your Offer</CardTitle>
          <CardDescription>Describe what you sell and the problems you solve.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="offer-description">Offer Description</Label>
              <FieldHint label="Why does the AI need an offer description?">
                Two to three sentences on what you sell and the outcome buyers get. The AI references this every time it pitches in a message — keep it concrete and specific.
              </FieldHint>
            </div>
            <textarea
              id="offer-description"
              rows={3}
              placeholder="Describe what you offer and the value it provides..."
              value={form.offerDescription}
              onChange={(e) => setForm((prev) => ({ ...prev, offerDescription: e.target.value }))}
              disabled={isLoading || isSaving}
              className={TEXTAREA_CLASS}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="offer-pain-point">Primary Pain Point</Label>
              <FieldHint label="What is the primary pain point used for?">
                The single biggest problem your offer solves. The AI uses this to open messages with "I see you're dealing with X" framing. Make it the pain a buyer would nod at on first read.
              </FieldHint>
            </div>
            <Input
              id="offer-pain-point"
              placeholder="The #1 problem your prospects face"
              value={form.primaryPainPoint}
              onChange={(e) => setForm((prev) => ({ ...prev, primaryPainPoint: e.target.value }))}
              disabled={isLoading || isSaving}
            />
          </div>

          <div className="flex items-start gap-1.5">
            <div className="flex-1">
              <ChipInput
                label="Additional Pain Points"
                placeholder="Add pain point..."
                chips={form.painPoints}
                onChange={setPainPoints}
                disabled={isLoading || isSaving}
              />
            </div>
            <div className="pt-7">
              <FieldHint label="When are additional pain points used?">
                Secondary pains the AI can pivot to when a lead's role / industry doesn't match the primary one. Adds variety across a 10-lead batch so messages don't all sound the same.
              </FieldHint>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="offer-social-proof">Social Proof</Label>
              <FieldHint label="How is social proof used?">
                Specific results, named clients, or numbers the AI can drop into FU2 when revealing the pitch. Use real data only — "200+ teams" works, "industry-leading" doesn't.
              </FieldHint>
            </div>
            <textarea
              id="offer-social-proof"
              rows={2}
              placeholder="Customer results, testimonials, numbers..."
              value={form.socialProof}
              onChange={(e) => setForm((prev) => ({ ...prev, socialProof: e.target.value }))}
              disabled={isLoading || isSaving}
              className={TEXTAREA_CLASS}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="offer-differentiator">Differentiator</Label>
              <FieldHint label="When does the differentiator show up?">
                The one thing competitors can't claim. Optionally injected as an 8-word "tease" clause in messages where a lead has tried similar tools and they didn't work.
              </FieldHint>
            </div>
            <Input
              id="offer-differentiator"
              placeholder="What makes you different from alternatives?"
              value={form.differentiator}
              onChange={(e) => setForm((prev) => ({ ...prev, differentiator: e.target.value }))}
              disabled={isLoading || isSaving}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="offer-one-liner">One-Liner</Label>
              <FieldHint label="What is the one-liner for?">
                Your offer in a single sentence — used as the "we help X do Y" backbone when the AI introduces the product in FU2 or in a reply.
              </FieldHint>
            </div>
            <Input
              id="offer-one-liner"
              placeholder="Your offer in one sentence"
              value={form.coreOfferOneLiner}
              onChange={(e) => setForm((prev) => ({ ...prev, coreOfferOneLiner: e.target.value }))}
              disabled={isLoading || isSaving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Credibility */}
      <Card>
        <CardHeader>
          <CardTitle>Credibility</CardTitle>
          <CardDescription>Build trust with case studies, resources, and proof of expertise.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Case Study */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="offer-has-case-study"
                checked={form.hasCaseStudy}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, hasCaseStudy: checked === true }))
                }
                disabled={isLoading || isSaving}
              />
              <label htmlFor="offer-has-case-study" className="text-sm cursor-pointer">
                I have a case study
              </label>
            </div>
            {form.hasCaseStudy && (
              <div className="space-y-2 pl-6">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="offer-case-study">Case Study Summary</Label>
                  <FieldHint label="When does the AI use the case study?">
                    One-line proof point the AI cites when revealing the pitch ("e.g. a similar RevOps lead at a Series C booked 14 meetings in 6 weeks"). Use a real, specific result.
                  </FieldHint>
                </div>
                <Input
                  id="offer-case-study"
                  placeholder="Brief summary of your best case study"
                  value={form.caseStudyOneLiner}
                  onChange={(e) => setForm((prev) => ({ ...prev, caseStudyOneLiner: e.target.value }))}
                  disabled={isLoading || isSaving}
                />
              </div>
            )}
          </div>

          {/* Shareable Resource */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="offer-has-resource"
                checked={form.hasShareableResource}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, hasShareableResource: checked === true }))
                }
                disabled={isLoading || isSaving}
              />
              <label htmlFor="offer-has-resource" className="text-sm cursor-pointer">
                I have a shareable resource (lead magnet)
              </label>
            </div>
            {form.hasShareableResource && (
              <div className="space-y-4 pl-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="offer-resource-desc">Resource Description</Label>
                    <FieldHint label="What is this resource used for?">
                      Short description of a free asset (audit, framework, teardown) the AI can offer as a Trojan-Horse opener. The lead replies "yes send it" → you've started a real conversation.
                    </FieldHint>
                  </div>
                  <Input
                    id="offer-resource-desc"
                    placeholder="What is the resource?"
                    value={form.resourceDescription}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, resourceDescription: e.target.value }))
                    }
                    disabled={isLoading || isSaving}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="offer-resource-link">Resource Link</Label>
                    <FieldHint label="When does the AI send the resource link?">
                      The URL the AI sends when a lead replies asking for the resource. Same flow as the meeting link — pasted verbatim into the reply.
                    </FieldHint>
                  </div>
                  <Input
                    id="offer-resource-link"
                    type="url"
                    placeholder="https://..."
                    value={form.resourceLink}
                    onChange={(e) => setForm((prev) => ({ ...prev, resourceLink: e.target.value }))}
                    disabled={isLoading || isSaving}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Authority */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="offer-authority">Authority / Expertise</Label>
              <FieldHint label="How is authority used?">
                Background that earns credibility — "15 years at Stripe and Brex", "advisor to 5 Series A teams", etc. The AI weaves a piece of this into senior-track FU2 messages when a peer-level frame helps.
              </FieldHint>
            </div>
            <textarea
              id="offer-authority"
              rows={2}
              placeholder="Years of experience, certifications, notable clients..."
              value={form.authorityProof}
              onChange={(e) => setForm((prev) => ({ ...prev, authorityProof: e.target.value }))}
              disabled={isLoading || isSaving}
              className={TEXTAREA_CLASS}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(form)} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Offer Settings'
          )}
        </Button>
        {savedSuccess && (
          <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
            <CheckCircle2 className="h-4 w-4" />
            Saved successfully
          </div>
        )}
        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      </div>

      {/* Tracked links (2026-09-05): clicks on the user's own booking page /
          website / deck, reusing the /r/<code> redirect. Offer links saved
          above are offered as one-click destinations. */}
      <TrackedLinksCard
        suggestions={[
          { label: 'Shareable resource', url: form.resourceLink },
        ].filter((s) => typeof s.url === 'string' && s.url.trim().length > 0)}
      />
    </div>
  )
}
